import React, { useCallback, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useInbox } from '../../services/queries/useInbox';
import { InboxItem } from '../../services/api/inboxApi';

interface InboxListProps {
  onOpenItem: (itemId: string) => void;
}

export function InboxList({ onOpenItem }: InboxListProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const { data, isLoading, isError, refetch, isRefetching } = useInbox(50, 0);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  const renderItem = useCallback(
    ({ item }: { item: InboxItem }) => {
      const unread = item.read_at === null;
      return (
        <TouchableOpacity style={styles.row} onPress={() => onOpenItem(item.id)}>
          <View style={styles.dotCol}>{unread && <View style={styles.dot} />}</View>
          <View style={styles.contentCol}>
            <Text style={[styles.title, unread && styles.titleUnread]} numberOfLines={1}>
              {item.title}
            </Text>
            {!!item.preview && (
              <Text style={styles.preview} numberOfLines={2}>
                {item.preview}
              </Text>
            )}
          </View>
        </TouchableOpacity>
      );
    },
    [styles, onOpenItem]
  );

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }
  if (isError) {
    return (
      <View style={styles.center}>
        <Text style={styles.empty}>
          {t('errors.failedToLoad', { item: t('tabs.inbox', { defaultValue: 'Inbox' }) })}
        </Text>
      </View>
    );
  }
  return (
    <FlatList
      style={styles.list}
      contentContainerStyle={styles.listContent}
      data={data?.items ?? []}
      keyExtractor={(i) => i.id}
      renderItem={renderItem}
      refreshControl={
        <RefreshControl
          refreshing={isRefetching}
          onRefresh={refetch}
          tintColor={theme.colors.primary}
        />
      }
      ListEmptyComponent={
        <View style={styles.center}>
          <Text style={styles.empty}>{t('inbox.empty', { defaultValue: 'No reports yet' })}</Text>
        </View>
      }
    />
  );
}

function createStyles(theme: any) {
  return StyleSheet.create({
    center: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 32,
      backgroundColor: theme.colors.background,
    },
    row: {
      flexDirection: 'row',
      paddingVertical: 14,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    dotCol: { width: 18, alignItems: 'center', paddingTop: 5 },
    dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: theme.colors.primary },
    contentCol: { flex: 1 },
    title: { fontSize: theme.typography.fontSizeMd, color: theme.colors.text },
    titleUnread: { fontWeight: theme.typography.fontWeightBold },
    preview: {
      marginTop: 2,
      fontSize: theme.typography.fontSizeSm,
      color: theme.colors.textSecondary,
    },
    empty: { fontSize: theme.typography.fontSizeMd, color: theme.colors.textSecondary },
    list: { flex: 1, backgroundColor: theme.colors.background },
    listContent: { padding: 16, flexGrow: 1 },
  });
}
